import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import { BusinessRuleItemType } from '@chiroup/core/constants/globals';
import {
  ConnectorConsoleExchange,
  ConnectorCredentialVerdict,
  ConnectorExecution,
  ConnectorPollingState,
  ConnectorRotationMetadata,
  ConnectorRunLockInfo,
  ConnectorSecretStatus,
  ConnectorVendorStatus,
  QueueDisposition,
  UnroutedQueueList,
} from '@chiroup/core/types/ConnectorAdmin.type';
import {
  ConnectorTraceConfig,
  ConnectorTraceFilters,
  ConnectorTraceRow,
} from '@chiroup/core/types/ConnectorTrace.type';
import {
  ConnectorManualRun,
  ConnectorManualRunKickResult,
  ConnectorQueuePurgeResult,
} from '@chiroup/core/types/Integration.type';
import { createConnectionApiMock } from './connectionApi.mock';

/** Close/reopen return: enough to patch the cached row (no refetch). */
export type UnroutedDispositionChangeResult = {
  exchangeId: string;
  disposition: string;
  change: BusinessRuleItemType;
  /**
   * Server-computed for the row's NEW disposition (false on close; the two-gate
   * answer on reopen). Patched into the cached row so a reopened Unsupported row's
   * Reprocess button reflects reality without a refetch.
   */
  reprocessable: boolean;
};

export type ConnectionApiHealth = {
  ok: boolean;
  service: string;
  /** Target DATABASE (the ${STAGE}/chiroup/database secret): dev/staging/prod. */
  stage: string;
  /** Where this process runs — with `stage`, the polling "combo" the UI shows. */
  execution: ConnectorExecution;
  version: string;
};

/** The one console read: everything the ops page paints. */
export type ConnectionApiConsole = {
  service: ConnectionApiHealth;
  vendors: ConnectorVendorStatus[];
  traceConfigs: Record<string, { config: ConnectorTraceConfig | null; active: boolean }>;
  transmit: {
    pending: Record<string, number>;
    locks: Record<string, ConnectorRunLockInfo | null>;
  };
  errors: { rows: ConnectorTraceRow[] };
  webhooks: { rings: ConnectorTraceRow[] };
  /** Day-rolled inbound-drain activity (`drain-activity:*` dedup family, INFRA-6). */
  drains: { activity: ConnectorTraceRow[] };
  /** Durable manual Receive/Transmit lifecycle, including quiet runs. */
  runs: ConnectorManualRun[];
  /** Dead-prune detector: oldest sink row vs the ONE retention number. */
  activitySink: { oldestMs: number | null; retentionDays: number };
};

/**
 * Ops-console client for connection-api (plan §3g). The `connectionApi`
 * Amplify REST entry is registered in main.tsx; the admin id token rides
 * every call automatically.
 */
const connectionApiService = () => {
  const vendorPath = (vendor: string, integrationId: number) =>
    `/connector/credentials/${encodeURIComponent(vendor)}/${integrationId}`;
  const tracePath = (vendor: string, integrationId: number) =>
    `/connector/trace/${encodeURIComponent(vendor)}/${integrationId}`;

  // ONE read paints the whole console (plan §3f: as few routes as reasonable).
  // `includeReviewed` un-hides rows a human marked reviewed.
  const consoleStatus = (includeReviewed = false): Promise<ConnectionApiConsole> => {
    return ChiroUpAPI.get(
      'connectionApi',
      '/connector/console',
      includeReviewed ? { queryParams: { includeReviewed: '1' } } : {}
    );
  };

  const runCredentialCheck = (
    vendor?: string
  ): Promise<{
    results: Record<string, ConnectorCredentialVerdict>;
  }> => {
    return ChiroUpAPI.post('connectionApi', '/connector/credential-check/run', {
      body: vendor ? { vendor } : {},
    });
  };

  const stageCandidate = (
    vendor: string,
    integrationId: number,
    apiKey: string
  ): Promise<{ status: ConnectorSecretStatus; rotation: ConnectorRotationMetadata }> => {
    return ChiroUpAPI.post('connectionApi', `${vendorPath(vendor, integrationId)}/candidate`, {
      body: { apiKey },
    });
  };

  const validateCandidate = (
    vendor: string,
    integrationId: number
  ): Promise<{ valid: boolean; rotation: ConnectorRotationMetadata }> => {
    return ChiroUpAPI.post(
      'connectionApi',
      `${vendorPath(vendor, integrationId)}/validate-candidate`,
      { body: {} }
    );
  };

  const promoteCandidate = (
    vendor: string,
    integrationId: number
  ): Promise<{ status: ConnectorSecretStatus; rotation: ConnectorRotationMetadata }> => {
    return ChiroUpAPI.post('connectionApi', `${vendorPath(vendor, integrationId)}/promote`, {
      body: {},
    });
  };

  const rollback = (
    vendor: string,
    integrationId: number
  ): Promise<{ status: ConnectorSecretStatus; rotation: ConnectorRotationMetadata }> => {
    return ChiroUpAPI.post('connectionApi', `${vendorPath(vendor, integrationId)}/rollback`, {
      body: {},
    });
  };

  const setTraceConfig = (
    vendor: string,
    integrationId: number,
    body: { filters?: ConnectorTraceFilters; durationMinutes: number }
  ): Promise<{ config: ConnectorTraceConfig; active: boolean }> => {
    return ChiroUpAPI.post('connectionApi', tracePath(vendor, integrationId), { body });
  };

  const traceLogs = (
    vendor: string,
    integrationId: number,
    params: { sinceId?: number; limit?: number; includeReviewed?: boolean }
  ): Promise<{ rows: ConnectorTraceRow[] }> => {
    return ChiroUpAPI.get('connectionApi', `${tracePath(vendor, integrationId)}/logs`, {
      queryParams: {
        sinceId: String(params.sinceId ?? 0),
        limit: String(params.limit ?? 100),
        ...(params.includeReviewed ? { includeReviewed: '1' } : {}),
      },
    });
  };

  const stampExchange = (
    exchangeId: string,
    durationMinutes: number
  ): Promise<{ exchangeId: string; traceUntil: number }> => {
    return ChiroUpAPI.post(
      'connectionApi',
      `/connector/trace-exchange/${encodeURIComponent(exchangeId)}`,
      { body: { durationMinutes } }
    );
  };

  // ── Inbound queues (plan §3e.7) — one list endpoint, ONE disposition per
  //    read (INFRA-10.2 item 4): only the visible queue's rows come back,
  //    bounded, with all three totals for the tab badges. ────────────────────
  const listUnrouted = (disposition: QueueDisposition): Promise<UnroutedQueueList> => {
    return ChiroUpAPI.get('connectionApi', '/connector/queues', {
      queryParams: { disposition },
    });
  };

  const assignUnrouted = (
    exchangeId: string,
    clinicId: number
  ): Promise<{ exchangeId: string; clinicId: number }> => {
    return ChiroUpAPI.post(
      'connectionApi',
      `/connector/queues/${encodeURIComponent(exchangeId)}/assign`,
      { body: { clinicId } }
    );
  };

  const closeUnrouted = (
    exchangeId: string,
    reason: string
  ): Promise<UnroutedDispositionChangeResult> => {
    return ChiroUpAPI.post(
      'connectionApi',
      `/connector/queues/${encodeURIComponent(exchangeId)}/close`,
      { body: { reason } }
    );
  };

  const reopenUnrouted = (
    exchangeId: string,
    message: string
  ): Promise<UnroutedDispositionChangeResult> => {
    return ChiroUpAPI.post(
      'connectionApi',
      `/connector/queues/${encodeURIComponent(exchangeId)}/reopen`,
      { body: { message } }
    );
  };

  // ── Transmit backstop + always-recorded errors (plan §3c.1a / §3g) ──────
  // Manual runs are vendor-scoped; the scheduled cron owns fleet sweeps.
  const transmitRun = (vendor: string): Promise<ConnectorManualRunKickResult> => {
    return ChiroUpAPI.post('connectionApi', '/connector/transmit/run', {
      body: { vendor },
    });
  };

  /**
   * Per-vendor "run inbound drain now" — schedules the worker and returns
   * immediately; the console refresh shows what landed.
   */
  const inboundDrainRun = (vendor: string): Promise<ConnectorManualRunKickResult> => {
    return ChiroUpAPI.post('connectionApi', '/connector/inbound-drain/run', {
      body: { vendor },
    });
  };

  /**
   * Error-detail modal read: pull ONE exchange by id (dispatched on the console
   * GET, no new route) — returns the exchange + its SSOT `retryable` flag.
   */
  const fetchExchange = (
    exchangeId: string
  ): Promise<{ exchange: ConnectorConsoleExchange | null }> => {
    return ChiroUpAPI.get('connectionApi', '/connector/console', {
      queryParams: { fetch: 'exchange', id: exchangeId },
    });
  };

  /**
   * The modal's Download-payload button (item 8): the stored document as
   * pretty-printed JSON, dispatched on the same console GET. Only offered when
   * the exchange read said `hasPayload`.
   */
  const fetchExchangePayload = (
    exchangeId: string
  ): Promise<{ exchangeId: string; payload: string; contentType: string }> => {
    return ChiroUpAPI.get('connectionApi', '/connector/console', {
      queryParams: { fetch: 'payload', id: exchangeId },
    });
  };

  /**
   * Re-drive ONE exchange (the retired inbound-redispatch route folds in here):
   * inbound re-dispatch or outbound re-enqueue, gated by the SSOT. `trace`
   * stamps the exchange first ("Retry + Trace").
   */
  const retryExchange = (
    exchangeId: string,
    trace = false
  ): Promise<{
    exchangeId: string;
    outcome: string;
    direction: 'inbound' | 'outbound';
    traced: boolean;
  }> => {
    return ChiroUpAPI.put('connectionApi', '/connector/console', {
      body: { function: 'retry-exchange', exchangeId, trace },
    });
  };

  /**
   * Push ONE `Unsupported` inbound exchange back through the funnel now that its
   * parser/handler exist. Re-enters at ROUTING, so the outcome is `processed` when the
   * clinic resolves and `unrouted` when it does not — the latter is a real answer, not
   * a failure: the document is understood, its owner is not.
   */
  const reprocessExchange = (
    exchangeId: string
  ): Promise<{ exchangeId: string; outcome: string; reason?: string; clinicId?: number }> => {
    return ChiroUpAPI.put('connectionApi', '/connector/console', {
      body: { function: 'reprocess-exchange', exchangeId },
    });
  };

  /**
   * Sweep EVERY queued document of one type — the post-deploy drain. `remaining`
   * reports what is STILL queued, so a capped run is visible rather than looking
   * like the queue emptied.
   */
  const reprocessDocType = (
    docType: string,
    limit?: number
  ): Promise<{
    docType: string;
    attempted: number;
    processed: number;
    unrouted: number;
    failed: { exchangeId: string; message: string }[];
    remaining: number;
  }> => {
    return ChiroUpAPI.put('connectionApi', '/connector/console', {
      body: { function: 'reprocess-exchange', docType, limit },
    });
  };

  /**
   * Mark an activity row `reviewed` (hide it from its feed) or restore it.
   * Dispatched on the ONE console-mutation route.
   */
  const reviewActivity = (
    id: number,
    reviewed: boolean
  ): Promise<{ id: number; reviewed: boolean; changed: boolean }> => {
    return ChiroUpAPI.put('connectionApi', '/connector/console', {
      body: { function: 'review-activity', id, reviewed },
    });
  };

  const clearTransmitLock = (vendor: string): Promise<{ cleared: string }> => {
    return ChiroUpAPI.post('connectionApi', '/connector/transmit/run', {
      body: { clearLock: vendor },
    });
  };

  /**
   * Empty the transmit FIFO (ghost-message cleanup). Pending exchange rows
   * survive — the per-vendor Transmit button re-enqueues them afterward.
   */
  const purgeTransmitQueue = (): Promise<ConnectorQueuePurgeResult> => {
    return ChiroUpAPI.post('connectionApi', '/connector/transmit/run', {
      body: { purgeQueue: true },
    });
  };

  /**
   * Turn AUTOMATION polling on/off for the CALLER'S OWN combo. PUT to the ONE
   * console-mutation route, dispatched by `function` (no per-action Lambda). The
   * backend fills in the execution (it knows if it's local or deployed) and the
   * database it's connected to; the client only says which vendor and on/off.
   */
  const togglePolling = (
    vendor: string,
    enabled: boolean
  ): Promise<{
    vendor: string;
    execution: ConnectorExecution;
    pollingEnabled: ConnectorPollingState;
  }> => {
    return ChiroUpAPI.put('connectionApi', '/connector/console', {
      body: { function: 'toggle-polling', vendor, enabled },
    });
  };

  return {
    consoleStatus,
    runCredentialCheck,
    stageCandidate,
    validateCandidate,
    promoteCandidate,
    rollback,
    setTraceConfig,
    traceLogs,
    stampExchange,
    listUnrouted,
    assignUnrouted,
    closeUnrouted,
    reopenUnrouted,
    transmitRun,
    inboundDrainRun,
    clearTransmitLock,
    purgeTransmitQueue,
    fetchExchange,
    fetchExchangePayload,
    reprocessDocType,
    reprocessExchange,
    retryExchange,
    reviewActivity,
    togglePolling,
  };
};

export type ConnectionApiService = ReturnType<typeof connectionApiService>;

/**
 * DEV-ONLY escape hatch to exercise the ops console UI without a backend:
 * append `?use-case=<scenario>` to the page URL (evaluated once at page
 * load). The single `import.meta.env.DEV && ...` expression is statically
 * false in production builds, so the mock factory reference — and with it
 * the whole mock module — is tree-shaken out of the shipped bundle.
 */
const service: ConnectionApiService =
  import.meta.env.DEV && new URLSearchParams(window.location.search).get('use-case')
    ? createConnectionApiMock()
    : connectionApiService();

export default service;
